/** 11sight aded packages END */
import { getToolbarButtons } from '../base/config/functions.web';
import { MEET_FEATURES } from '../base/jwt/constants';
import { isJwtFeatureEnabled } from '../base/jwt/functions';
import KeyboardShortcutsButton from '../keyboard-shortcuts/components/web/KeyboardShortcutsButton';
import { isScreenMediaShared } from '../screen-share/functions';
import { isWhiteboardVisible } from '../whiteboard/functions';
// import VideoSettingsButton from './components/web/VideoSettingsButton';
import { MAIN_TOOLBAR_BUTTONS_PRIORITY, TOOLBAR_TIMEOUT } from './constants';
import AudioSettingsButton from "../../11sight/ui/components/toolbox/AudioSettingsButton";
import VideoSettingsButton from "../../11sight/ui/components/toolbox/VideoSettingsButton";
import VideoBackgroundCircleButton from "../../11sight/ui/components/toolbox/VideoBackgroundCircleButton";
import ParticipantsPaneButton from "../../11sight/features/participants-pane/components/ParticipantsPaneButton";
import AudioOnlyButton from "../../11sight/ui/components/toolbox/AudioOnlyButton";
import { hasAvailableDevices } from "../base/devices/functions.web";
import { IGUMPendingState } from "../base/media/types";
import CameraFlipButton from "../../11sight/ui/components/toolbox/CameraFlipButton";
import HideCameraOffButton from "../../11sight/ui/components/toolbox/HideCameraOffButton";
import PictureInPictureButton from "../../11sight/ui/components/toolbox/PictureInPictureButton";
import NoiseSuppressionButton from "../noise-suppression/components/NoiseSuppressionButton";
import ClosedCaptionButton from "../../11sight/features/subtitles/components/web/ClosedCaptionButton";
import RecordButton from "../recording/components/Recording/web/RecordButton";
import LiveStreamButton from "../recording/components/LiveStream/web/LiveStreamButton";
import SpeakerStatsButton from "../speaker-stats/components/web/SpeakerStatsButton";
import ChatButton from "../chat/components/web/ChatButton";
import SecurityDialogButton from "../security/components/security-dialog/web/SecurityDialogButton";
import InviteButton from "../invite/components/add-people-dialog/web/InviteButton";
import TileViewButton from "../video-layout/components/TileViewButton";
import ToggleCameraButton from "./components/web/ToggleCameraButton";
import VideoQualityButton from "../video-quality/components/VideoQualityButton.web";
import FullscreenButton from "./components/web/FullscreenButton";
import LinkToSalesforceButton from "./components/web/LinkToSalesforceButton";
import SharedVideoButton from "../shared-video/components/web/SharedVideoButton";
import ShareAudioButton from "../screen-share/components/web/ShareAudioButton";
import WhiteboardButton from "../whiteboard/components/web/WhiteboardButton";
import SharedDocumentButton from "../etherpad/components/SharedDocumentButton.web";
import VideoBackgroundButton from "../virtual-background/components/VideoBackgroundButton";
import SettingsButton from "../settings/components/web/SettingsButton";
import EmbedMeetingButton from "../embed-meeting/components/EmbedMeetingButton";
import FeedbackButton from "../feedback/components/FeedbackButton.web";
import DownloadButton from "./components/DownloadButton";
import HelpButton from "./components/HelpButton";
import CustomOptionButton from "./components/web/CustomOptionButton";
import ProfileButton from "./components/web/ProfileButton";
import ShareDesktopButton from "./components/web/ShareDesktopButton";
import RaiseHandContainerButton from "../reactions/components/web/RaiseHandContainerButtons";
import ReactionsMenuButton from "../reactions/components/web/ReactionsMenuButton";
export * from './functions.any';
/**
 * Helper for getting the height of the toolbox.
 *
 * @returns {number} The height of the toolbox.
 */
export function getToolboxHeight() {
    const toolbox = document.getElementById('new-toolbox');
    return toolbox?.clientHeight || 0;
}
/**
 * Indicates if a toolbar button is enabled.
 *
 * @param {string} name - The name of the setting section as defined in
 * interface_config.js.
 * @param {IReduxState} state - The redux state.
 * @returns {boolean|undefined} - True to indicate that the given toolbar button
 * is enabled, false - otherwise.
 */
export function isButtonEnabled(name, state) {
    const toolbarButtons = getToolbarButtons(state);
    return toolbarButtons.indexOf(name) !== -1;
}
/**
 * Indicates if the toolbox is visible or not.
 *
 * @param {IReduxState} state - The state from the Redux store.
 * @returns {boolean} - True to indicate that the toolbox is visible, false -
 * otherwise.
 */
export function isToolboxVisible(state) {
    const { iAmRecorder, iAmSipGateway, toolbarConfig } = state['features/base/config'];
    const { alwaysVisible } = toolbarConfig || {};
    const { timeoutID, visible } = state['features/toolbox'];
    const { audioSettingsVisible, videoSettingsVisible } = state['features/settings'];
    const whiteboardVisible = isWhiteboardVisible(state);
    return Boolean(!iAmRecorder && !iAmSipGateway
        && (timeoutID
            || visible
            || alwaysVisible
            || audioSettingsVisible
            || videoSettingsVisible
            || whiteboardVisible));
}
/**
 * Indicates if the audio settings button is disabled or not.
 *
 * @param {IReduxState} state - The state from the Redux store.
 * @returns {boolean}
 */
export function isAudioSettingsButtonDisabled(state) {
    return !(hasAvailableDevices(state, 'audioInput')
        || hasAvailableDevices(state, 'audioOutput'))
        || state['features/base/config'].startSilent;
}
/**
 * Indicates if the desktop share button is disabled or not.
 *
 * @param {IReduxState} state - The state from the Redux store.
 * @returns {boolean}
 */
export function isDesktopShareButtonDisabled(state) {
    const { muted, unmuteBlocked } = state['features/base/media'].video;
    const videoOrShareInProgress = !muted || isScreenMediaShared(state);
    const enabledInJwt = isJwtFeatureEnabled(state, MEET_FEATURES.SCREEN_SHARING, true);
    return !enabledInJwt || (unmuteBlocked && !videoOrShareInProgress);
}
/**
 * Indicates if the video settings button is disabled or not.
 *
 * @param {IReduxState} state - The state from the Redux store.
 * @returns {boolean}
 */
export function isVideoSettingsButtonDisabled(state) {
    return !hasAvailableDevices(state, 'videoInput') || !window.APP?.room?.conference_options?.use_camera;
}
/**
 * Indicates if the video mute button is disabled or not.
 *
 * @param {IReduxState} state - The state from the Redux store.
 * @returns {boolean}
 */
export function isVideoMuteButtonDisabled(state) {
    const { muted, unmuteBlocked, gumPending } = state['features/base/media'].video;
    return !hasAvailableDevices(state, 'videoInput')
        || (unmuteBlocked && Boolean(muted))
        || gumPending !== IGUMPendingState.NONE
        || !window.APP?.room?.conference_options?.use_camera;
}
/**
 * If an overflow drawer should be displayed or not.
 * This is usually done for mobile devices or on narrow screens.
 *
 * @param {IReduxState} state - The state from the Redux store.
 * @returns {boolean}
 */
export function showOverflowDrawer(state) {
    return state['features/toolbox'].overflowDrawer;
}
/**
 * Returns true if the overflow menu button is displayed and false otherwise.
 *
 * @param {IReduxState} state - The state from the Redux store.
 * @returns {boolean} - True if the overflow menu button is displayed and false otherwise.
 */
export function showOverflowMenu(state) {
    return state['features/toolbox'].overflowMenuVisible;
}
/**
 * Indicates whether the toolbox is enabled or not.
 *
 * @param {IReduxState} state - The state from the Redux store.
 * @returns {boolean}
 */
export function isToolboxEnabled(state) {
    return state['features/toolbox'].enabled;
}
/**
 * Returns the toolbar timeout from config or the default value.
 *
 * @param {IReduxState} state - The state from the Redux store.
 * @returns {number} - Toolbar timeout in milliseconds.
 */
export function getToolbarTimeout(state) {
    const { toolbarConfig } = state['features/base/config'];
    return toolbarConfig?.timeout || TOOLBAR_TIMEOUT;
}
/**
 * Returns all buttons that could be rendered.
 *
 * @param {Object} _customToolbarButtons - An array containing custom buttons objects.
 * @returns {Object} The button maps mainMenuButtons and overflowMenuButtons.
 */
export function getAllToolboxButtons(_customToolbarButtons) {
    const microphone = {
        key: "microphone",
        Content: AudioSettingsButton,
        group: 0,
        alignment: "center"
    };
    const camera = {
        key: "camera",
        Content: VideoSettingsButton,
        group: 0,
        alignment: "center"
    };
    const profile = {
        key: "profile",
        Content: ProfileButton,
        group: 0
    };
    const chat = {
        key: "chat",
        Content: ChatButton,
        group: 0,
        alignment: "start"
    };
    const desktop = {
        key: "desktop",
        Content: ShareDesktopButton,
        group: 0,
        alignment: "start"
    };
    const participants = {
        key: "participants-pane",
        Content: ParticipantsPaneButton,
        group: 0,
        alignment: "end"
    };
    const invite = {
        key: "invite",
        Content: InviteButton,
        group: 0,
        alignment: "end"
    };
    const tileview = {
        key: "tileview",
        Content: TileViewButton,
        group: 0,
        alignment: "end"
    };
    const toggleCamera = {
        key: "toggle-camera",
        Content: ToggleCameraButton,
        group: 0
    };
    const videoQuality = {
        key: "videoquality",
        Content: VideoQualityButton,
        group: 0
    };
    const fullscreen = {
        key: "fullscreen",
        Content: FullscreenButton,
        group: 0
    };
    const security = {
        key: "security",
        alias: "info",
        moderatorOnly: true,
        Content: SecurityDialogButton,
        group: 0,
        alignment: "start"
    };
    const cc = {
        key: "closedcaptions",
        Content: ClosedCaptionButton,
        group: 0
    };
    const recording = {
        key: "recording",
        Content: RecordButton,
        group: 0,
        moderatorOnly: true
    };
    const livestreaming = {
        key: "livestreaming",
        Content: LiveStreamButton,
        group: 0
    };
    const linkToSalesforce = {
        key: "linktosalesforce",
        Content: LinkToSalesforceButton,
        group: 0
    };
    const shareVideo = {
        key: "sharedvideo",
        Content: SharedVideoButton,
        group: 0
    };
    const shareAudio = {
        key: "shareaudio",
        Content: ShareAudioButton,
        group: 0
    };
    const noiseSuppression = {
        key: "noisesuppression",
        Content: NoiseSuppressionButton,
        group: 0
    };
    const whiteboard = {
        key: "whiteboard",
        Content: WhiteboardButton,
        group: 0
    };
    const etherpad = {
        key: "etherpad",
        Content: SharedDocumentButton,
        group: 0
    };
    const virtualBackground = {
        key: "select-background",
        Content: VideoBackgroundButton,
        mobileOnly: true,
        group: 0
    };
    const speakerStats = {
        key: "stats",
        Content: SpeakerStatsButton,
        group: 0,
        moderatorOnly: true
    };
    const settings = {
        key: "settings",
        Content: SettingsButton,
        group: 0
    };
    const shortcuts = {
        key: 'shortcuts',
        Content: KeyboardShortcutsButton,
        group: 4
    };
    const embed = {
        key: "embedmeeting",
        Content: EmbedMeetingButton,
        group: 0
    };
    const feedback = {
        key: "feedback",
        Content: FeedbackButton,
        group: 0
    };
    const download = {
        key: "download",
        Content: DownloadButton,
        group: 0
    };
    const help = {
        key: "help",
        Content: HelpButton,
        group: 0
    };
    const customButtons = _customToolbarButtons?.reduce((prev, { backgroundColor, icon, id, text }) => {
        return {
            ...prev,
            [id]: {
                backgroundColor,
                key: id,
                Content: CustomOptionButton,
                group: 0,
                icon,
                text
            }
        };
    }, {});
    const pip = {
        key: "pip",
        Content: PictureInPictureButton,
        group: 0
    };
    const hideCameraOff = {
        key: "hide-camera-off",
        Content: HideCameraOffButton,
        group: 0
    };
    const cameraFlip = {
        key: "camera-flip",
        Content: CameraFlipButton,
        group: 0,
        alignment: "topRight",
        mobileOnly: true
    };
    const virtualBackgroundCircle = {
        key: "select-background-circle",
        Content: VideoBackgroundCircleButton,
        alignment: "center",
        desktopOnly: true,
        group: 0
    };
    const reactions = {
        key: "reactions",
        Content: ReactionsMenuButton,
        group: 0
    };
    const raisehand = {
        key: "raisehand",
        Content: RaiseHandContainerButton,
        group: 0
    };
    const audioOnly = {
        key: "audio-only",
        Content: AudioOnlyButton,
        group: 0,
        alignment: "start"
    };
    return {
        noisesuppression: noiseSuppression,
        noiseSuppression,
        virtualBackgroundCircle,
        microphone,
        camera,
        cameraFlip,
        desktop,
        audioOnly,
        chat,
        raisehand,
        reactions,
        participants,
        invite,
        tileview,
        toggleCamera,
        videoQuality,
        fullscreen,
        pip,
        security,
        cc,
        recording,
        livestreaming,
        linkToSalesforce,
        shareVideo,
        shareAudio,
        whiteboard,
        etherpad,
        speakerStats,
        hideCameraOff,
        settings,
        embed,
        feedback,
        download,
        help,
        ...customButtons
    };
}
/**
 * Sets the notify click mode for the buttons.
 *
 * @param {Object} buttons - The list of toolbar buttons.
 * @param {Map} buttonsWithNotifyClick - The buttons notify click configuration.
 * @returns {void}
 */
function setButtonsNotifyClickMode(buttons, buttonsWithNotifyClick) {
    if (typeof APP === 'undefined' || (buttonsWithNotifyClick?.size ?? 0) <= 0) {
        return;
    }
    Object.values(buttons).forEach((button) => {
        if (typeof button === 'object') {
            button.notifyMode = buttonsWithNotifyClick.get(button.key);
        }
    });
}
/**
 * Returns all buttons that need to be rendered.
 *
 * @param {IGetVisibleButtonsParams} params - The parameters needed to extract the visible buttons.
 * @returns {Object} - The visible buttons arrays .
 */
export function getVisibleButtons({ allButtons, buttonsWithNotifyClick, toolbarButtons, clientWidth, jwtDisabledButtons, mainToolbarButtonsThresholds }) {
    setButtonsNotifyClickMode(allButtons, buttonsWithNotifyClick);
    const filteredButtons = Object.keys(allButtons).filter(key => typeof key !== 'undefined' // filter invalid buttons that may be coming from config.mainToolbarButtons
        // override
        && !jwtDisabledButtons.includes(key)
        && toolbarButtons.includes(key));
    const { order } = mainToolbarButtonsThresholds.find(({ width }) => clientWidth > width)
        || mainToolbarButtonsThresholds[mainToolbarButtonsThresholds.length - 1];
    const mainToolbarButtonKeysOrder = [
        ...order.filter(key => filteredButtons.includes(key)),
        ...MAIN_TOOLBAR_BUTTONS_PRIORITY.filter(key => !order.includes(key) && filteredButtons.includes(key)),
        ...filteredButtons.filter(key => !order.includes(key) && !MAIN_TOOLBAR_BUTTONS_PRIORITY.includes(key))
    ];
    const mainButtonsKeys = mainToolbarButtonKeysOrder.slice(0, order.length);
    const overflowMenuButtons = filteredButtons.reduce((acc, key) => {
        if (!mainButtonsKeys.includes(key)) {
            acc.push(allButtons[key]);
        }
        return acc;
    }, []);
    // if we have 1 button in the overflow menu it is better to directly display it in the main toolbar by replacing
    // the "More" menu button with it.
    if (overflowMenuButtons.length === 1) {
        const button = overflowMenuButtons.shift()?.key;
        button && mainButtonsKeys.push(button);
    }
    const mainMenuButtons = mainButtonsKeys.map(key => allButtons[key]);
    return {
        mainMenuButtons,
        overflowMenuButtons
    };
}
/**
 * Returns the list of participant menu buttons that have that notify the api when clicked.
 *
 * @param {Object} state - The redux state.
 * @returns {Map<string, NOTIFY_CLICK_MODE>} - The list of participant menu buttons.
 */
export function getParticipantMenuButtonsWithNotifyClick(state) {
    return state['features/toolbox'].participantMenuButtonsWithNotifyClick;
}
/**
 * Returns the time, timing function and delay for elements that are position above the toolbar and need to move along
 * with the toolbar.
 *
 * @param {boolean} isToolbarVisible - Whether the toolbar is visible or not.
 * @returns {ICSSTransitionObject}
 */
export function getTransitionParamsForElementsAboveToolbox(isToolbarVisible) {
    // The transition time and delay is different to account for the time when the toolbar is about to hide/show but
    // the elements don't have to move.
    return isToolbarVisible ? {
        duration: 0.15,
        easingFunction: 'ease-in',
        delay: 0.15
    } : {
        duration: 0.24,
        easingFunction: 'ease-in',
        delay: 0
    };
}
/**
 * Converts a given object to a css transition value string.
 *
 * @param {ICSSTransitionObject} object - The object.
 * @returns {string}
 */
export function toCSSTransitionValue(object) {
    const { delay, duration, easingFunction } = object;
    return `${duration}s ${easingFunction} ${delay}s`;
}
